import React, { useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { Card, Modal, Button } from "react-bootstrap";

// Bucket → color
const COLOR_MAP = {
  "Force 3DS": "oklch(76.9% 0.188 70.08)",
  "Prefer 3DS": "#3B82F6",
  "Bypass 3DS": "#10B981",
  "BLOCKED": "oklch(63.7% 0.237 25.331)",
  "BLACKLISTED": "oklch(45.2% 0.211 324.591)",
  "Other 3DS": "#9CA3AF",
};

const DEFAULT_COLORS = ["#3B82F6", "#10B981", "#F97316", "#6366F1"];

// Label uses BACKEND percent
const renderLabel = ({ payload }) => {
  const pct = payload?.pct;
  if (!pct || pct <= 0) return null;
  return `${payload.name} (${pct.toFixed(1)}%)`;
};

// Tooltip uses BACKEND percent
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        padding: 8,
        fontSize: 12,
        borderRadius: 4,
        color: "#111827",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontWeight: 600 }}>{row.name}</div>
      <div>{`${row.value.toLocaleString()} events`}</div>
      <div style={{ color: "#6b7280" }}>{`${row.pct.toFixed(2)}%`}</div>
    </div>
  );
};

const ThreeDSAnalytics = ({ data, loading }) => {
  const [showHelp, setShowHelp] = useState(false);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-4">
          <span style={{ fontSize: 12, color: "#6B7280" }}>
            Loading 3DS data…
          </span>
        </Card.Body>
      </Card>
    );
  }

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 0 }}>
            No 3DS policy data available for this period.
          </p>
        </Card.Body>
      </Card>
    );
  }

  // -----------------------------
  // NORMALIZE + BACKEND %
  // -----------------------------
  const total = data.reduce((sum, d) => sum + Number(d.value || 0), 0);

  const chartData = data.map((d) => {
    const value = Number(d.value || 0);
    const pct = d.percent != null
      ? Number(d.percent)
      : total > 0
      ? (value / total) * 100
      : 0;

    return { ...d, value, pct };
  });

  // -----------------------------
  // EXEC SUMMARY NUMBERS
  // -----------------------------
  const map = Object.fromEntries(
    chartData.map((d) => [(d.name || "").toUpperCase(), d.value])
  );

  const bypass = map["BYPASS 3DS"] || 0;
  const prefer = map["PREFER 3DS"] || 0;
  const force = map["FORCE 3DS"] || 0;
  const blocked = map["BLACKLISTED"] || 0;

  const bypassPct = total > 0 ? (bypass / total) * 100 : 0;
  const stepUpPct = total > 0 ? ((prefer + force) / total) * 100 : 0;
  const blockedPct = total > 0 ? (blocked / total) * 100 : 0;

  return (
    <>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 style={{ fontWeight: 700, fontSize: 14 }}>
                3DS Outcomes &amp; Policy Levels
              </h6>
              <p style={{ fontSize: 12, color: "#64748B", marginBottom: 0 }}>
                Total events: <strong>{total.toLocaleString()}</strong>
              </p>
            </div>

            <Button
              size="sm"
              variant="light"
              onClick={() => setShowHelp(true)}
              style={{ borderRadius: 999, fontSize: 11 }}
            >
              ?
            </Button>
          </div>

          {/* EXEC SUMMARY */}
          <div className="d-flex gap-2 flex-wrap mb-3">
            <span className="badge bg-light text-dark">
              ~{bypassPct.toFixed(0)}% frictionless
            </span>
            <span className="badge bg-light text-dark">
              {stepUpPct.toFixed(0)}% step-up auth
            </span>
            <span className="badge bg-warning text-dark">
              {blockedPct.toFixed(1)}% blocked
            </span>
          </div>

          {/* DONUT */}
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={80}
                  outerRadius={120}
                  labelLine={false}
                  label={false}
                >
                  {chartData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={
                        COLOR_MAP[entry.name] ||
                        DEFAULT_COLORS[i % DEFAULT_COLORS.length]
                      }
                    />
                  ))}
                </Pie>

                {/* CENTER KPI */}
                <text
                  x="50%"
                  y="48%"
                  textAnchor="middle"
                  style={{ fontSize: 24, fontWeight: 900 }}
                >
                  {total.toLocaleString()}
                </text>
                <text
                  x="50%"
                  y="58%"
                  textAnchor="middle"
                  style={{ fontSize: 12, fill: "#64748B" }}
                >
                  Total events
                </text>

                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* POLICY DISTRIBUTION */}
          <div className="mt-3">
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>
              POLICY DISTRIBUTION
            </div>

            {[...chartData]
              .sort((a, b) => b.value - a.value)
              .map((row, i) => {
                const color =
                  COLOR_MAP[row.name] ||
                  DEFAULT_COLORS[i % DEFAULT_COLORS.length];

                return (
                  <div key={i} className="mt-2">
                    <div className="d-flex justify-content-between">
                      <span style={{ fontWeight: 600 }}>{row.name}</span>
                      <span style={{ fontSize: 12 }}>
                        {row.pct.toFixed(2)}% ({row.value.toLocaleString()})
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        background: "#E5E7EB",
                        borderRadius: 999,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${row.pct}%`,
                          height: "100%",
                          background: color,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </Card.Body>
      </Card>

      {/* HELP MODAL */}
      <Modal show={showHelp} onHide={() => setShowHelp(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14 }}>
            How 3DS decisions are made
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>
          <ul>
            <li><b>Bypass 3DS</b> for low-risk traffic</li>
            <li><b>Prefer 3DS</b> for medium risk</li>
            <li><b>Force 3DS</b> for high risk</li>
            <li><b>Blocked</b> traffic is temporarily blocked due to high risk</li>
            <li><b>Blacklisted</b> traffic is blocked outright</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShowHelp(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ThreeDSAnalytics;
